<template>
    <div class="proposal-detail" data-theme="v1">
        <!-- Header -->
        <detail-page-header
            :title="selectedProposal.building.address_line_1"
            :subtitle="`${selectedProposal.building.city}, ${selectedProposal.building.state}`"
            :show-back-button="true"
            :badges="headerBadges"
            color-variant="green"
            back-button-label="Back to Project"
            @back="goBackToProject"
        >
            <template #actions>
                <themed-button
                    v-if="canCreateRevision"
                    variant="outline"
                    size="sm"
                    :loading="loading"
                    @click="createRevision">
                    <theme-icon icon="plus" type="icons" classes="w-4 h-4 flex" />
                    <span>New Revision</span>
                </themed-button>
                <themed-button
                    v-if="canUpdateProposal"
                    variant="default"
                    size="sm"
                    :loading="loading"
                    @click="saveProposal">
                    <theme-icon icon="save-icon" type="icons" classes="w-4 h-4 flex" />
                    <span>Save Proposal</span>
                </themed-button>
            </template>
        </detail-page-header>

        <div id="proposal-container" class="proposal-detail__content">
            <div class="proposal-detail__container">
                <a name="pictures"></a>
                <div class="proposal-detail__media-section">
                    <div class="proposal-detail__gallery">
                        <media-gallery-v1
                            :documentable-id="selectedProposal.building.id"
                            documentable-type="App\Building"
                            :media="selectedProposal.building.media"
                            :height="340"
                            :show-media-manager="false">
                        </media-gallery-v1>
                    </div>
                    
                    <div class="proposal-detail__summary-wrapper">
                        <table class="proposal-detail__summary-table">
                                    <tbody>
                                        <tr>
                                            <td class="pl-4 py-1 font-semibold">Rentable Sq. Ft.</td>
                                            <td class="py-1">
                                                <number-input name="total_square_feet" :size="10" v-model="selectedProposal.total_square_feet" classes="text-sm text-right py-1" justify-end></number-input>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td class="pl-4 py-1 font-semibold">Loss Factor (%)</td>
                                            <td class="py-1">
                                                <percent-input name="loss_factor_percentage" :size="5" v-model="selectedProposal.loss_factor_percentage" add-classes="text-sm text-right py-1" justify-end></percent-input>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td class="pl-4 py-1 font-semibold">Usable Sq. Ft.</td>
                                            <td class="py-1 text-sm text-right font-medium">
                                                <span class="pr-2">{{ usableSqFt | number }}</span>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td class="pl-4 py-1 font-semibold">Base Rent ($)</td>
                                            <td class="py-1 text-sm text-right font-medium">
                                                <span class="pr-2">{{ formatCurrency(baseRent, 2) }}</span>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td class="pl-4 py-1 font-semibold">Term (Years)</td>
                                            <td class="py-1">
                                                <number-input name="term" :size="5" v-model="selectedProposal.term" classes="text-sm text-right py-1" justify-end></number-input>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td class="pl-4 py-1 font-semibold"># Free Rent Months</td>
                                            <td class="py-1 text-sm text-right font-medium">
                                                <span class="pr-2">{{ totalNumberFreeMonths }}</span>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td class="pl-4 py-1 font-semibold">Months in Term</td>
                                            <td class="py-1 text-sm text-right font-medium">
                                                <span class="pr-2">{{ monthsInTerm }}</span>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td class="pl-4 py-1 font-semibold">Incl. Free Months</td>
                                            <td class="py-1 flex justify-end text-sm text-right font-medium">
                                                <div class="pr-2 flex items-center">
                                                    <input type="checkbox" class="w-5 h-5" v-model="selectedProposal.include_free_months" />
                                                    <span class="ml-2">{{ selectedProposal.include_free_months ? 'Yes' : 'No' }}</span>
                                                </div>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>

                                <table class="proposal-detail__summary-table">
                                    <tbody>
                                        <tr>
                                            <td class="pl-4 py-1 font-semibold">Total Rent</td>
                                            <td class="pl-10 py-1 text-sm text-right font-medium">
                                                <span class="pr-2">{{ formatCurrency(totalRent, 0) }}</span>
                                            </td>
                                            <td class="pl-10 py-1 font-semibold">Annual Avg $</td>
                                            <td class="pl-10 py-1 text-sm text-right font-medium">
                                                <span class="pr-2">{{ formatCurrency(annualAverageCost, 0) }}</span>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td class="pl-4 py-1 font-semibold">Total Op. Ex.</td>
                                            <td class="py-1 text-sm text-right font-medium">
                                                <span class="pr-2">{{ formatCurrency(totalOperatingExpenses, 0) }}</span>
                                            </td>
                                            <td class="pl-10 py-1 font-semibold">Monthly Avg $</td>
                                            <td class="py-1 text-sm text-right font-medium">
                                                <span class="pr-2">{{ formatCurrency(monthlyAverageCost, 0) }}</span>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td class="pl-4 py-1 font-semibold">Total Lease $</td>
                                            <td class="py-1 text-sm text-right font-medium">
                                                <span class="pr-2">{{ formatCurrency(totalLeaseCost, 0) }}</span>
                                            </td>
                                            <td class="pl-10 py-1 font-semibold">Eff. Rent PSF</td>
                                            <td class="py-1 text-sm text-right font-medium">
                                                <span class="pr-2">{{ formatCurrency(effectiveRentPSF, 2) }}</span>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td class="pl-4 py-1 font-semibold">Discount Rate</td>
                                            <td class="py-1">
                                                <percent-input name="discount_rate" :size="5" v-model="selectedProposal.discount_rate" add-classes="text-sm text-right py-1" justify-end></percent-input>
                                            </td>
                                            <td class="pl-10 py-1 font-semibold">Average PUSF</td>
                                            <td class="py-1 text-sm text-right font-medium">
                                                <span class="pr-2">{{ formatCurrency(averagePUSF, 2) }}</span>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td class="pl-4 py-1 font-semibold">NPV</td>
                                            <td class="py-1 text-sm text-right font-medium">
                                                <span class="pr-2">{{ formatCurrency(npv, 0) }}</span>
                                            </td>
                                            <td colspan="2"></td>
                                        </tr>
                                        <tr>
                                            <td class="pl-4 py-1 font-semibold">TI $ Req. PSF</td>
                                            <td class="py-1">
                                                <currency-input name="ti_required_psf" :size="6" v-model="selectedProposal.ti_required_psf" add-classes="text-sm text-right py-1" is-decimal justify-end></currency-input>
                                            </td>
                                            <td colspan="2"></td>
                                        </tr>
                                        <tr>
                                            <td class="pl-4 py-1 font-semibold">TI $ Offered PSF</td>
                                            <td class="py-1">
                                                <currency-input name="ti_offered_psf" :size="6" v-model="selectedProposal.ti_offered_psf" add-classes="text-sm text-right py-1" is-decimal justify-end></currency-input>
                                            </td>
                                            <td colspan="2"></td>
                                        </tr>
                                        <tr>
                                            <td class="pl-4 py-1 font-semibold">TI $ to Amort.</td>
                                            <td class="py-1 text-sm text-right font-medium">
                                                <span class="pr-2">{{ formatCurrency(tiToAmortization, 2) }}</span>
                                            </td>
                                            <td colspan="2"></td>
                                        </tr>
                                        <tr>
                                            <td class="pl-4 py-1 font-semibold">TI $ Tenant</td>
                                            <td class="pl-10 py-1 text-sm text-right font-medium">
                                                <span class="pr-2">{{ formatCurrency(tiTenantCost, 0) }}</span>
                                            </td>
                                            <td colspan="2"></td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="proposal-detail__table-wrapper">
                        <table class="proposal-detail__table">
                            <thead class="proposal-detail__thead">
                                <tr>
                                    <th class="proposal-detail__th"></th>
                                    <th class="proposal-detail__th proposal-detail__th--center">Years</th>
                                    <th v-for="row in termData" :key="row.year" class="proposal-detail__th proposal-detail__th--center">{{ row.year }}</th>
                                    <th class="proposal-detail__th proposal-detail__th--right">Totals</th>
                                </tr>
                            </thead>
                            <tbody class="proposal-detail__tbody">
                                <tr class="proposal-detail__tr">
                                    <td class="proposal-detail__td">
                                        <div class="proposal-detail__expense-header">
                                            <span class="proposal-detail__expense-label">Expense</span>
                                            <themed-button v-if="canCreateExpense" variant="outline" size="sm" @click="$modal.show('new-proposal-expense')" class="proposal-detail__icon-btn proposal-detail__icon-btn--success">
                                                <theme-icon icon="plus" type="icons" classes="w-4 h-4 flex" />
                                            </themed-button>
                                        </div>
                                    </td>
                                    <td class="proposal-detail__td proposal-detail__td--right proposal-detail__td--semibold">Free Months</td>
                                    <td v-for="row in termData" :key="row.year" class="proposal-detail__td">
                                        <themed-select 
                                            v-model="row.number_free_months" 
                                            :options="freeMonthsOptions"
                                            @input="saveFreeMonths"
                                            class="proposal-detail__themed-select"
                                        />
                                    </td>
                                    <td class="proposal-detail__td"></td>
                                </tr>
                                <tr class="proposal-detail__tr proposal-detail__tr--totals">
                                    <td class="proposal-detail__td proposal-detail__td--total">Totals</td>
                                    <td class="proposal-detail__td proposal-detail__td--total"></td>
                                    <td class="proposal-detail__td proposal-detail__td--total proposal-detail__td--right" v-for="row in termData" :key="row.year">{{ formatCurrency(row.total, 0) }}</td>
                                    <td class="proposal-detail__td proposal-detail__td--total proposal-detail__td--right">{{ formatCurrency(totalLeaseCost, 0) }}</td>
                                </tr>
                            </tbody>
                            <tbody v-for="expense in expenses" :key="expense.id" class="proposal-detail__tbody">
                                <tr class="proposal-detail__tr">
                                    <td class="proposal-detail__td">
                                        <div class="proposal-detail__expense-row">
                                            <span>{{ expense.type.name }}</span>
                                            <div class="proposal-detail__expense-actions">
                                                <themed-button v-if="canUpdateProposal" variant="outline" size="sm" @click="openEditExpense(expense.id)" class="proposal-detail__icon-btn">
                                                    <theme-icon icon="edit-pencil"  classes="w-4 h-4 flex" type="icons" />
                                                </themed-button>
                                                <themed-button v-if="canDeleteExpense" variant="outline" size="sm" @click="confirmDeleteExpense(expense.id)" class="proposal-detail__icon-btn proposal-detail__icon-btn--delete">
                                                    <theme-icon icon="trash" classes="w-4 h-4 flex" type="icons" />
                                                </themed-button>
                                            </div>
                                        </div>
                                    </td>
                                    <td class="proposal-detail__td">
                                        <themed-button v-if="canUpdateProposal" variant="outline" size="sm" @click="openEditEscalations(expense.id)" class="proposal-detail__escalations-btn">
                                            <theme-icon icon="edit" type="icons" classes="w-4 h-4 flex"/>
                                            <span>Escalations</span>
                                        </themed-button>                                        
                                    </td>
                                    <td class="proposal-detail__td proposal-detail__td--right" v-for="i in selectedProposal.term" :key="i">
                                        {{ getEscalationForYear(expense, i) }}
                                    </td>
                                    <td class="proposal-detail__td proposal-detail__td--highlight"></td>
                                </tr>
                                <tr class="proposal-detail__tr">
                                    <td class="proposal-detail__td"></td>
                                    <td class="proposal-detail__td">
                                        <div class="proposal-detail__expense-details">
                                            <span>{{ expense.base_net_included }}</span>
                                            <span>{{ formatCurrency(expense.amount, expense.frequency == 'PSF' ? 2 : 0) }}</span>
                                        </div>
                                    </td>
                                    <td class="proposal-detail__td proposal-detail__td--right" v-for="row in expense.cost_by_term_year" :key="row.year">
                                        {{ formatCurrency(row.total, 0) }}
                                    </td>
                                    <td class="proposal-detail__td proposal-detail__td--total proposal-detail__td--right">
                                        {{ formatCurrency(getExpenseTotal(expense), 0) }}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    
                    <div v-if="canDeleteProposal" class="proposal-detail__delete-section">
                        <themed-button variant="destructive" size="sm" @click="confirmDeleteProposal">
                            <theme-icon icon="trash" type="icons" classes="w-4 h-4 flex" />
                            <span>Delete Proposal</span>
                        </themed-button>
                    </div>
                
                <new-proposal-expense @save-proposal="saveProposal"></new-proposal-expense>
                <edit-proposal-expense @save-proposal="saveProposal"></edit-proposal-expense>
                <edit-proposal-expense-escalations @save-proposal="saveProposal"></edit-proposal-expense-escalations>

                <notifications classes="notification" position="bottom right"></notifications>
            </div>
</template>

<script>
    import { mapGetters, mapState } from 'vuex';
    import NewProposalExpense from './NewProposalExpense.vue';
    import EditProposalExpense from './EditProposalExpense.vue';
    import EditProposalExpenseEscalations from './EditProposalExpenseEscalations.vue';
    import ThemedButton from '../ThemedButton.vue';
    import ThemedSelect from '../ThemedSelect.vue';
    import ThemeIcon from '../ThemeIcon.vue';
    import DetailPageHeader from '../DetailPageHeader.vue';
    import MediaGalleryV1 from '../MediaGallery.vue';
    
    export default {
        name: 'ThemedProposal',

        components: {
            NewProposalExpense,
            EditProposalExpense,
            EditProposalExpenseEscalations,
            ThemedButton,
            ThemedSelect,
            ThemeIcon,
            DetailPageHeader,
            MediaGalleryV1
        },

        props: ['data'],

        data() {
            return { 
                termData: {},
                editing: false,
                loading: false
            }
        },

        created() {
            this.proposals.all.push(this.data);
            this.proposals.filtered.push(this.data);
            this.proposals.selectedProposalId = this.data.id;

            _.map(this.data.expenses, expense => {
                expense.cost_by_term_year = JSON.parse(expense.cost_by_term_year);
            });

            let rent = _.find(this.selectedProposal.expenses, ['type_id', 1]);
            this.termData = _.map([...Array(parseInt(this.data.term)).keys()], year => {
                let numberFreeMonths = 0;

                if (rent) {
                    numberFreeMonths = _.filter(rent.free_months, ['year_number', year + 1]).length;
                }

                return {
                    'year': year + 1,
                    'number_free_months': numberFreeMonths,
                    'total': 0
                }
            });

            this.recalculateYearlyTotals();
        },

        watch: {
            'selectedProposal.expenses': {
                handler(newValue, oldValue) {
                    this.recalculateYearlyTotals();
                },
                deep: true
            }
        },

        computed: {
            ...mapState(['projects']),
            ...mapState(['proposals']),
            ...mapGetters(['selectedProposal']),

            canCreateRevision() {
                return this.$gate.can('create', 'project');
            },

            canUpdateProposal() {
                return this.$gate.can('update', 'project', this.selectedProposal.project_id);
            },

            headerBadges() {
                const badges = [];
                
                // Add revision number badge with warning color
                if (this.selectedProposal.revision_number) {
                    badges.push({
                        text: `Revision #${this.selectedProposal.revision_number}`,
                        class: 'badge-warning'
                    });
                }
                
                return badges;
            },

            canDeleteProposal() {
                return this.$gate.can('delete', 'project', this.selectedProposal.project_id);
            },

            canCreateExpense() {
                return this.$gate.can('create', 'project');
            },
            canDeleteExpense() {
                return this.$gate.can('delete', 'project', this.selectedProposal.project_id);
            },

            freeMonthsOptions() {
                return [
                    { value: 0, label: '0' },
                    ...Array.from({ length: 12 }, (_, i) => ({
                        value: i + 1,
                        label: String(i + 1)
                    }))
                ];
            },

            usableSqFt() {
                let rentable = Number.prototype.cleanseInteger(this.selectedProposal.total_square_feet);
                let lossFactor = Number.prototype.cleanseInteger(this.selectedProposal.loss_factor_percentage);

                return rentable - (rentable * (lossFactor / 100));
            },

            baseRent() {
                let rent = _.find(this.selectedProposal.expenses, ['type_id', 1]);

                return rent?.amount || 0;
            },

            expenses() {
                return _.sortBy(this.selectedProposal.expenses, [expense => { return expense.type.name != 'Rent'; }]);
            },

            monthsInTerm() {
                let termYears = parseInt(this.selectedProposal.term);

                if (Number.isInteger(termYears)) {
                    return (termYears * 12) + (this.selectedProposal.include_free_months ? 0 : this.totalNumberFreeMonths);
                }

                return (this.selectedProposal.include_free_months ? 0 : this.totalNumberFreeMonths);
            },

            totalNumberFreeMonths() {
                return _.sumBy(this.termData, row => parseInt(row.number_free_months));
            },

            totalRent() {
                let rent = _.find(this.selectedProposal.expenses, ['type_id', 1]);

                if (rent) {
                    return _.map(rent.cost_by_term_year, row => row.total).reduce((partialSum, a) => partialSum + a, 0);
                }

                return 0;
            },

            totalOperatingExpenses() {
                let expenses = _.filter(this.selectedProposal.expenses, expense => expense.type_id != 1);

                if (expenses) {
                    return _.flatMap(expenses, expense => {
                        return _.map(expense.cost_by_term_year, row => row.total)
                    }).reduce((partialSum, a) => partialSum + a, 0);
                }

                return 0;
            },

            totalLeaseCost() {
                return this.totalRent + this.totalOperatingExpenses;
            },

            npv() {
                let npv = 0;
                let discount_rate = Number.prototype.cleanseInteger(this.selectedProposal.discount_rate) / 100;                
                
                this.termData.forEach(row => {
                    npv += row.total / (Math.pow(1 + discount_rate, row.year + 1));
                });

                return npv;
            },

            tiToAmortization() {
                return ( Number.prototype.cleanseFloat(this.selectedProposal.ti_required_psf) -  Number.prototype.cleanseFloat(this.selectedProposal.ti_offered_psf)) * 100;
            },

            tiTenantCost() {
                return this.selectedProposal.total_square_feet * this.tiToAmortization;
            },

            annualAverageCost() {
                if (this.selectedProposal.term == 0) {
                    return 0;
                }
                
                return this.totalLeaseCost / this.selectedProposal.term;
            },

            monthlyAverageCost() {
                if (this.selectedProposal.monthsInTerm == 0) {
                    return 0;
                }
                
                return this.totalLeaseCost / this.monthsInTerm;
            },

            effectiveRentPSF() {
                if (this.selectedProposal.term == 0) {
                    return 0;
                }
                
                return (this.totalLeaseCost / this.selectedProposal.term) / parseInt(this.selectedProposal.total_square_feet);
            },

            averagePUSF() {
                if (this.usableSqFt == 0 || this.monthsInTerm == 0) {
                    return 0;
                }
                
                return (this.totalLeaseCost / (this.monthsInTerm / 12)) / parseInt(this.usableSqFt);
            }
        },

        methods: {
            goBackToProject() {
                window.location.href = `/projects/${this.selectedProposal.project_id}`;
            },

            recalculateYearlyTotals() {
                this.termData.forEach(row => {
                    row.total = _.map(this.selectedProposal.expenses, expense => {
                        return expense.cost_by_term_year[row.year].total;
                    }).reduce((partialSum, a) => partialSum + a, 0);
                });
            },

            getEscalationForYear(expense, yearNumber) {
                let escalation = _.find(expense.escalations, ['year_number', yearNumber]);

                return escalation?.amount || null;
            },

            getExpenseTotal(expense) {
                return _.map(expense.cost_by_term_year, row => {
                    return row.total;
                }).reduce((partialSum, a) => partialSum + a, 0);
            },

            openEditExpense(expenseId) {
                this.proposals.selectedProposalExpenseId = expenseId;

                this.$modal.show('edit-proposal-expense');
            },

            openEditEscalations(expenseId) {
                this.proposals.selectedProposalExpenseId = expenseId;

                this.$modal.show('edit-proposal-expense-escalations');
            },

            async saveProposal() {
                this.loading = true;

                this.selectedProposal.total_lease_cost = this.totalLeaseCost;
                this.selectedProposal.effective_rent_prsf = this.effectiveRentPSF;
                this.selectedProposal.average_rent_pusf = this.averagePUSF;
                this.selectedProposal.average_monthly_cost = this.monthlyAverageCost;
                this.selectedProposal.npv = this.npv;

                await this.$store.dispatch('saveProposal').then(() => {
                    this.loading = false;

                    this.$notify({
                        type: 'success',
                        title: 'Success',
                        text: "Proposal Saved"
                    });
                });
            },

            confirmDeleteExpense(expenseId) {
                this.$swal({
                    title: 'Delete this expense?',
                    text: "ALL data will be lost",
                    type: 'warning',
                    buttonsStyling: false,
                    confirmButtonText: 'Yes - Delete it',
                    confirmButtonClass: 'btn btn-red',
                    showCancelButton: true,
                    cancelButtonClass: 'btn btn-secondary mr-4',
                    reverseButtons: true
                }).then((result) => {
                    if (result.value) {
                        this.$store.dispatch('deleteExpense', expenseId).then(() => {
                            this.saveProposal();
                        });
                    }
                });
            },

            async saveFreeMonths() {
                let rent = _.find(this.selectedProposal.expenses, ['type_id', 1]);

                if (rent) {
                    rent.free_months = [];

                    this.termData.forEach(row => {
                        let numberFreeMonths = row.number_free_months;

                        _.each([...Array(parseInt(numberFreeMonths)).keys()], month => {
                            rent.free_months.push({
                                'expense_id': rent.id,
                                'year_number': row.year,
                                'month_number': month + 1
                            });
                        });
                    });

                    this.proposals.selectedProposalExpenseId = rent.id;

                    await this.$store.dispatch('saveExpense').then(() => {
                        this.$notify({
                            type: 'success',
                            title: 'Success',
                            text: "Free Months Saved"
                        });

                        this.saveProposal();
                    });
                }
            },

            async createRevision() {
                this.loading = true;

                await this.$store.dispatch('createRevision').then(() => {
                    this.loading = false;

                    this.$notify({
                        type: 'success',
                        title: 'Success',
                        text: "Revision Created"
                    });
                });
            },

            confirmDeleteProposal() {
                this.$swal({
                    title: 'Delete this proposal?',
                    text: "ALL data will be lost",
                    type: 'warning',
                    buttonsStyling: false,
                    confirmButtonText: 'Yes - Delete it',
                    confirmButtonClass: 'btn btn-red',
                    showCancelButton: true,
                    cancelButtonClass: 'btn btn-secondary mr-4',
                    reverseButtons: true
                }).then(async (result) => {
                    if (result.value) {
                        await this.$store.dispatch('deleteProposal').then(() => {
                            this.$notify({
                                type: 'success',
                                title: 'Proposal Deleted',
                                text: "Redirecting..."
                            });

                            setTimeout(() => {
                                window.location = '/projects/' + this.selectedProposal.project_id
                            }, 1300);
                        });
                    }
                });
            },
        }
    }
</script>